/**
 * Saved grids, in IndexedDB.
 *
 * Safari can evict the storage of a site that has not been visited for seven days.
 * Installing the app on the home screen makes that much less likely but does not
 * rule it out, so this store is treated as disposable: everything it holds can be
 * exported to a file, and nothing else in the app depends on it surviving.
 */
import type { GameKey } from '../engine';

const DB_NAME = 'lotomatrix';
const DB_VERSION = 1;
const STORE = 'grids';

export interface SavedGrid {
  /** Random id, also the primary key. */
  id: string;
  game: GameKey;
  main: number[];
  bonus: number[];
  /** ISO date of the draw this grid is meant for. */
  drawDate: string;
  /** ISO instant the grid was saved. */
  savedAt: string;
  /** Lambda actually used, kept so a grid can be explained later. */
  lambda: number;
  /** Popularity index of the main numbers at save time. */
  popularity: number;
  note?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise !== null) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponible'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byGameAndDraw', ['game', 'drawDate']);
        store.createIndex('byDrawDate', 'drawDate');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('ouverture IndexedDB refusée'));
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('opération IndexedDB refusée'));
      }),
  );
}

export function newId(): string {
  const buf = new Uint8Array(8);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function listGrids(): Promise<SavedGrid[]> {
  const all = await tx<SavedGrid[]>('readonly', (store) => store.getAll() as IDBRequest<SavedGrid[]>);
  return all.sort((a, b) => {
    if (a.drawDate !== b.drawDate) return a.drawDate < b.drawDate ? 1 : -1;
    return a.savedAt < b.savedAt ? 1 : -1;
  });
}

export async function saveGrid(grid: SavedGrid): Promise<void> {
  await tx('readwrite', (store) => store.put(grid));
}

export async function saveGrids(grids: readonly SavedGrid[]): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    const store = transaction.objectStore(STORE);
    for (const g of grids) store.put(g);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('écriture refusée'));
  });
}

export async function deleteGrid(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id));
}

export async function clearGrids(): Promise<void> {
  await tx('readwrite', (store) => store.clear());
}

/** Everything in the store, as a JSON string, so nothing is lost to an eviction. */
export async function exportGrids(): Promise<string> {
  const grids = await listGrids();
  return JSON.stringify({ app: 'lotomatrix', version: 1, exportedAt: new Date().toISOString(), grids }, null, 2);
}

export async function importGrids(json: string): Promise<number> {
  const parsed: unknown = JSON.parse(json);
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !Array.isArray((parsed as { grids?: unknown }).grids)
  ) {
    throw new Error('fichier non reconnu');
  }
  const grids = (parsed as { grids: SavedGrid[] }).grids.filter(
    (g) => typeof g.id === 'string' && Array.isArray(g.main) && typeof g.game === 'string',
  );
  await saveGrids(grids);
  return grids.length;
}
